import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
    WritableSignal,
    signal,
    ChangeDetectionStrategy,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import {
    DisplayErrorDialogComponent,
    DisplayErrorDialogData,
} from '@app/v2/components/dialogs/display-error-dialog/display-error-dialog.component';
import {
    MedpaceDraggableListWithCheckboxesComponent,
    RemoveOptionDataWithParentId,
} from '@components/atoms/medpace-draggable-list-with-checkboxes/medpace-draggable-list-with-checkboxes.component';
import { RemoveOptionData } from '@components/atoms/medpace-draggable-list/medpace-draggable-list.component';
import { MdsOption } from '@medpacesoftwaredevelopment/designsystem';
import { ClinTrakPersonViewModel, CountryRegionCtsmViewModel, StudyPersonLocaleViewModel } from '@models/clintrak';
import { Target } from '@models/event-objects/target';
import { Region } from '@models/region';
import { VisitGroup } from '@models/visitGroup';
import { AdminRequestServices } from '@services/admin/admin-request.service';
import { AdminStudyServices } from '@services/admin/admin-study.sevice';
import { ClinTrakDataService } from '@services/clintrak/clintrak-data.service';
import { LoadingViewTriggerService } from '@services/loading-module-trigger/loading-module-trigger.service';
import { ClinTrakDataStateService } from '@services/state-management/clintrak-data-state.service';
import { StudyStateService } from '@services/state-management/study-state.service';
import { TravelOptionStateService } from '@services/state-management/travel-option-state.service';
import { ModalSize, getModalConfig } from '@utility/modal-helper';
import { PersistentFormControl } from '@utility/persistent-forms';
import { rxjsEffect, sortAsc } from '@utility/utility';
import {
    NumberFormatValidator,
    beforeDateValidator,
    maxLengthValidator,
    notWhitespaceValidator,
    requiredAtLeastOneOptionCheckedArray,
} from '@utility/utility.validators';
import { cloneDeep } from 'lodash-es';
import { Moment } from 'moment';
import { Observable, Subject, defer, iif, of, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs';
import { MedpaceVisitMappingModalComponent } from 'src/app/components/molecules/modals/medpace-visit-mapping-modal/medpace-visit-mapping-modal.component';
import { InputChange } from 'src/app/models/event-objects/input-change';
import { Study, StudyInformation, StudyTravelOption, VisitName } from 'src/app/models/study';
export interface RemoveOptionDataWithIndexAndParentId extends RemoveOptionData<VisitName> {
    parentId: number;
    index: number;
}
@Component({
    selector: 'medpace-study-info-card',
    standalone: false,
    templateUrl: './study-info-card.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../../../organisms/input-card/input-card.component.scss', './study-info-card.component.scss'],
})
export class MedpaceStudyInfoCardComponent implements OnInit, OnDestroy {
    @Input()
    content: StudyInformation = null;

    content$: Observable<Study>;

    @Input()
    isClinTrakComparison: boolean = false;

    @Input()
    isEditing: boolean;

    @Input()
    parentFormGroup: FormGroup;

    studyExpenseFormGroup = new FormGroup({
        studyExpenses: new FormArray<FormControl<boolean>>([], requiredAtLeastOneOptionCheckedArray),
    });

    @Output()
    inputChangeEvent = new EventEmitter<InputChange>();

    @ViewChild('auto') autocompleteChild: MatAutocomplete;
    @ViewChild('regionInput') regionInput: ElementRef<HTMLInputElement>;
    @ViewChildren('visitNamesDraggableList') visitNamesDraggableList: QueryList<
        MedpaceDraggableListWithCheckboxesComponent<VisitName>
    >;
    regionControl = new UntypedFormControl();
    separatorKeysCodes: number[] = [ENTER, COMMA];
    selectable: boolean = true;
    removable: boolean = true;
    filteredRegions: Observable<Region[]>;
    showTravelOptions: WritableSignal<boolean> = signal(true);

    // regions: StudyRegion[];
    regionsForView: MdsOption[];
    countriesForView: MdsOption[];
    countries: CountryRegionCtsmViewModel[];
    regions: CountryRegionCtsmViewModel[];
    selectedRegions: CountryRegionCtsmViewModel[];
    selectedCountries: CountryRegionCtsmViewModel[];
    regionsFormGroup: FormGroup;
    countriesFormGroup = new FormGroup({
        countriesDropdown: new FormControl<string[]>([], Validators.required),
    });

    groupNameInput = new FormControl<string>('', [
        Validators.required,
        notWhitespaceValidator(),
        maxLengthValidator(100),
    ]);
    datesFormGroup: FormGroup<{ dateFPFV: PersistentFormControl<Moment>; dateLPLV: PersistentFormControl<Moment> }>;
    loaderSpinner: boolean = true;
    private componentDestroyed$: Subject<boolean> = new Subject();

    travelOptions: StudyTravelOption[];
    travelOptionsForView: MdsOption[];
    selectedTravelOptions: StudyTravelOption[];
    travelOptionsFormGroup: FormGroup;
    visitGroups: { [groupName: string]: VisitName[] } = {};
    backupVisitGroups: { [groupName: string]: VisitName[] } = {};
    multipleVisitRadioFormGroup: FormGroup;
    hasMultipleVisitSchedules: boolean = false;
    showVisitGroupField: boolean = false;
    cohortExists: boolean = false;
    isPrimaryGroupName: string = null;

    radioButtons = [
        {
            label: 'Yes',
            value: 'yes',
            name: `Yes`,
            id: `YesMultipleVisitSchedules`,
        },
        {
            label: 'No',
            value: 'no',
            name: `No`,
            id: `NoMultipleVisitSchedules`,
        },
    ];

    private readonly tryRemoveVisitNameSubject = new Subject<RemoveOptionDataWithIndexAndParentId>();
    private readonly tryRemoveVisitName$ = rxjsEffect(() =>
        this.tryRemoveVisitNameSubject.pipe(
            withLatestFrom(this.stateService.getStudy()),
            switchMap(([removeOptionData, study]) => {
                const isNewOption = !removeOptionData.optionToRemove.id; // no id - option just added, not yet saved
                const isNewOption$ = of({}).pipe(
                    tap((_) =>
                        this.visitNamesDraggableList
                            .toArray()
                            [removeOptionData.index].removeOption(removeOptionData.optionToRemove)
                    )
                ); // can safely remove newly added option, there is for sure no requests using it
                const isNotNewOption$ = defer(() => {
                    this.loadingViewTriggerService.openLoading();
                    return this.requestService
                        .getAllRequestsCount({
                            studyIds: [study.id],
                            visitNamesIds: [removeOptionData.optionToRemove.id],
                        }) // need to call api to check for requests
                        .pipe(
                            tap((_) => this.loadingViewTriggerService.closeLoading()),
                            tap((count) => {
                                if (count === 0)
                                    this.visitNamesDraggableList
                                        .toArray()
                                        [removeOptionData.index].removeOption(removeOptionData.optionToRemove);
                                else
                                    this.dialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(
                                        DisplayErrorDialogComponent,
                                        {
                                            ...getModalConfig(ModalSize.Small),
                                            data: {
                                                description: `Cannot remove visit type: ${removeOptionData.optionToRemove.name} because there are requests using it. You must first delete requests that utilize this visit type`,
                                            },
                                        }
                                    );
                            })
                        );
                });

                return iif(() => isNewOption, isNewOption$, isNotNewOption$);
            })
        )
    );

    private readonly tryRemoveVisitGroupSubject = new Subject<RemoveOptionDataWithIndexAndParentId>();
    private readonly tryRemoveVisitGroup$ = rxjsEffect(() =>
        this.tryRemoveVisitGroupSubject.pipe(
            withLatestFrom(this.stateService.getStudy()),
            switchMap(([removeOptionData, study]) => {
                const isNewGroup = !removeOptionData.parentId || !removeOptionData.optionToRemove?.visitGroup?.id; // no id - option just added, not yet saved
                const isNewGroup$ = of({}).pipe(
                    tap((_) => this.visitNamesDraggableList.toArray()[removeOptionData.index].removeGroup())
                ); // can safely remove newly added group, there is for sure no patient assigned to it
                const isNotNewGroup$ = defer(() => {
                    this.loadingViewTriggerService.openLoading();
                    return this.studyService
                        .getStudyPatients(study.id) // need to call api to check for patients
                        .pipe(
                            tap((_) => this.loadingViewTriggerService.closeLoading()),
                            tap((patients) => {
                                if (!patients.some((x) => x.visitGroupId === removeOptionData.parentId))
                                    this.visitNamesDraggableList.toArray()[removeOptionData.index].removeGroup();
                                else
                                    this.dialog.open<DisplayErrorDialogComponent, DisplayErrorDialogData>(
                                        DisplayErrorDialogComponent,
                                        {
                                            ...getModalConfig(ModalSize.Small),
                                            data: {
                                                description: `Cannot remove visit part/cohort: ${removeOptionData.optionToRemove.visitGroup.name} because there are patients assigned to it.`,
                                            },
                                        }
                                    );
                            })
                        );
                });

                return iif(() => isNewGroup, isNewGroup$, isNotNewGroup$);
            })
        )
    );

    constructor(
        private formBuilder: FormBuilder,
        private stateService: StudyStateService,
        private travelOptionStateService: TravelOptionStateService,
        private loadingViewTriggerService: LoadingViewTriggerService,
        private requestService: AdminRequestServices,
        private clinTrakDataStateService: ClinTrakDataStateService,
        private studyService: AdminStudyServices,
        private dialog: MatDialog,
        private clinTrakDataService: ClinTrakDataService
    ) {}

    ngOnInit(): void {
        this.parentFormGroup.addControl('studyExpensesFormGroup', this.studyExpenseFormGroup);

        this.clinTrakDataStateService
            .getCountries()
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((result) => {
                    this.countries = result;
                    this.countriesForView = result.map((x) => ({ value: x.countryId, viewValue: x.countryName }));

                    this.regions = result;
                    let distRegions: MdsOption[] = [];
                    result.forEach((cr) => {
                        if (!distRegions?.some((x) => x.value === cr.regionId))
                            distRegions.push({ value: cr.regionId, viewValue: cr.regionName });
                    });
                    this.regionsForView = distRegions;
                })
            )
            .subscribe();

        this.initializeFormGroup();

        this.content$ = this.stateService.getStudy();
        this.content$
            .pipe(
                tap((study) => {
                    if (study) {
                        this.content = study.studyInfo;
                        this.studyExpenseFormGroup.controls.studyExpenses.clear();
                        this.content.expenseTypes?.forEach((item) => {
                            const expenseControl = new FormControl(item.active);
                            this.studyExpenseFormGroup.controls.studyExpenses.push(expenseControl);
                        });

                        this.studyExpenseFormGroup.markAllAsTouched();
                        this.hasMultipleVisitSchedules = this.content.hasMultipleVisitSchedules;
                        this.initializePersonsArrays();
                        this.initRadioButtonsFormGroup();
                        this.setValues();
                        this.mapVisitsByGroup();
                        this.loaderSpinner = false;
                    }
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();

        this.travelOptionStateService
            .getTravelOptions()
            .pipe(
                tap((result) => {
                    this.travelOptions = result.map((x) => <StudyTravelOption>{ id: x.id, name: x.name, studyId: 0 });
                    this.travelOptionsForView = result.map((x) => ({ value: x.id, viewValue: x.name }));
                    this.handleAvailableTravelOptions();
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe();
    }

    initRadioButtonsFormGroup() {
        this.multipleVisitRadioFormGroup = this.formBuilder.group({
            multipleVisitControl: [this.content?.hasMultipleVisitSchedules ? 'yes' : 'no'],
        });
    }

    getRegionNamesByGuid(locales: StudyPersonLocaleViewModel[]): string {
        let regions = this.regionsForView.filter((x) => locales?.map((z) => z.regionId).includes(<string>x.value));
        let result = regions?.map((x) => x.viewValue).join(', ');
        return result;
    }

    hasMultipleVisitScheduleRadioChanged(value: string, target: Target) {
        this.hasMultipleVisitSchedules = value === 'yes' ? true : false;
        let visitName: VisitName = <VisitName>{
            name: '',
            scheduled: false,
            sortOrder: 1,
            visitGroup: <VisitGroup>{
                name: 'Visit Schedule',
                sortOrder: 1,
            },
        };

        if (!this.hasMultipleVisitSchedules) {
            let visitGroupsName = this.getVistGroupNamesAsArray();
            this.backupVisitGroups = this.visitGroups;

            if (Object.keys(this.visitGroups).length !== 0) {
                let value = this.visitGroups[visitGroupsName[0]];
                this.visitGroups = {};
                this.visitGroups[visitGroupsName[0]] = value;
            } else {
                this.visitGroups = {};

                visitGroupsName.forEach((groupName, index) => {
                    if (index !== 0) {
                        this.visitNamesDraggableList.toArray()[index].removeGroup();
                    }
                });
                this.visitGroups[visitName.visitGroup.name] = [visitName];
            }
        } else {
            if (Object.keys(this.backupVisitGroups).length !== 0) {
                this.visitGroups = this.backupVisitGroups;
            } else {
                if (Object.keys(this.visitGroups).length !== 0) {
                    let visitGroupsName = this.getVistGroupNamesAsArray();
                    let value = this.visitGroups[visitGroupsName[0]];
                    value.forEach((visit) => {
                        if (!visit.visitGroup) {
                            visit.visitGroup = visitName.visitGroup;
                        }
                    });

                    this.visitGroups[visitGroupsName[0]] = value;
                }
            }
        }

        this.emitChange(this.hasMultipleVisitSchedules, target);
    }

    addVisitScheduleGroup() {
        this.showVisitGroupField = true;
    }

    changeGroupName({ currentGroupName, newGroupName }) {
        this.visitGroups[newGroupName] = this.visitGroups[currentGroupName];
        delete this.visitGroups[currentGroupName];

        this.visitGroups[newGroupName].forEach((vn) => (vn.visitGroup.name = newGroupName));
    }

    isPrimaryChange({ groupName, isPrimary }) {
        this.isPrimaryGroupName = isPrimary ? groupName : null;
        let arrayList = this.visitNamesDraggableList.toArray();
        if (isPrimary) {
            arrayList.forEach((dL) => {
                let control = dL.getIsPrimaryControl();
                if (dL.optionName !== groupName) {
                    control.setValue(false);
                    this.changeIsPrimaryValueForGroupAndEmit(dL.optionName, false);
                }
            });
        } else {
            arrayList.forEach((dL) => {
                let control = dL.getIsPrimaryControl();
                if (dL.optionName !== groupName) control.enable();
            });
        }

        this.changeIsPrimaryValueForGroupAndEmit(groupName, isPrimary);
    }

    changeIsPrimaryValueForGroupAndEmit(groupName: string, isPrimary: boolean) {
        let visitsPerGroup = this.visitGroups[groupName];
        visitsPerGroup.forEach((v) => (v.visitGroup.isPrimary = isPrimary));
        let visits = <VisitName[]>[].concat(...Object.values(this.visitGroups));
        this.inputChangeEvent.emit({ target: 'visitTypes', value: visits });
    }

    addVisitGroup() {
        if (!this.groupNameInput.valid) {
            this.groupNameInput.markAsTouched();
            return;
        }
        let groupName = <string>this.groupNameInput.value;
        if (!this.getVistGroupNamesAsArray().some((x) => x === groupName)) {
            if (this.isPrimaryGroupName !== null) {
                let currentVisits = this.visitGroups[this.isPrimaryGroupName];
                let tempVisits = cloneDeep(currentVisits);
                let resultVisit = tempVisits.map((visit) => {
                    return <VisitName>{
                        name: visit.name,
                        scheduled: visit.scheduled,
                        sortOrder: visit.sortOrder,
                        visitGroup: { name: groupName, sortOrder: 1 },
                    };
                });
                this.visitGroups[groupName] = resultVisit;
                let allVisits = <VisitName[]>[].concat(...Object.values(this.visitGroups));
                this.inputChangeEvent.emit({ target: 'visitTypes', value: allVisits });
            } else {
                this.visitGroups[groupName] = [
                    <VisitName>{
                        name: '',
                        scheduled: false,
                        sortOrder: 1,
                        visitGroup: { name: groupName, sortOrder: 1 },
                    },
                ];
            }
            this.showVisitGroupField = false;
            this.cohortExists = false;
            this.groupNameInput.setValue('');
        } else this.cohortExists = true;
    }

    handleAvailableTravelOptions() {
        //all studies are Full Service: show travel options and keep all of them enabled
        this.showTravelOptions.set(true);
        this.travelOptionsForView?.forEach((val) => (val.disabled = false));
        //handle travelOptionsFormGroup.controls.travelOptionsDropdown validation
        this.handleTravelOptionsDropdownValidation();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next(true);
        this.componentDestroyed$.complete();
    }

    mapVisitsByGroup() {
        this.visitGroups = {};
        this.content?.visitTypes?.forEach((visit) => {
            if (!visit.visitGroup) visit.visitGroup = <VisitGroup>{ name: 'Visit Schedule', sortOrder: 1 };
            if (this.visitGroups[visit.visitGroup.name]) {
                if (!this.visitGroups[visit.visitGroup.name].includes(visit))
                    this.visitGroups[visit.visitGroup.name].push(visit);
            } else this.visitGroups[visit.visitGroup.name] = [visit];

            if (visit?.visitGroup?.isPrimary) this.isPrimaryGroupName = visit.visitGroup.name;
        });
    }

    getVistGroupNamesAsArray(): string[] {
        return Object.keys(this.visitGroups);
    }

    getVistNamesByGroupName(groupName: string): VisitName[] {
        return this.visitGroups[groupName];
    }

    removeVisitGroup(groupName: string) {
        delete this.visitGroups[groupName];
        let visits = <VisitName[]>[].concat(...Object.values(this.visitGroups));
        this.inputChangeEvent.emit({ target: 'visitTypes', value: visits });
    }

    isValidVisitsFormGroup(): boolean {
        let isValid = true;
        if (this.getVistGroupNamesAsArray().length > 0) {
            this.getVistGroupNamesAsArray().forEach((groupName) => {
                if (this.parentFormGroup.contains(groupName + 'Group')) {
                    if (!this.parentFormGroup.controls[groupName + 'Group'].valid) {
                        isValid = false;
                    }
                } else {
                    isValid = false;
                }
            });
        }

        return isValid;
    }

    openVisitMappingModal() {
        this.stateService
            .getStudy()
            .pipe(
                take(1),
                switchMap((study) =>
                    this.clinTrakDataService.getStudyVisits(study.studyCode).pipe(
                        switchMap((visits) =>
                            this.dialog
                                .open<MedpaceVisitMappingModalComponent>(MedpaceVisitMappingModalComponent, {
                                    id: 'MappingDialog',
                                    ...getModalConfig(visits?.length > 0 ? ModalSize.Medium : ModalSize.Small),
                                    data: { visits: this.content?.visitTypes },
                                })
                                .afterClosed()
                                .pipe(
                                    tap(({ mappedVisits, id }) => {
                                        if (mappedVisits && id)
                                            this.inputChangeEvent.emit({ target: id, value: mappedVisits });
                                    })
                                )
                        )
                    )
                )
            )

            .subscribe();
    }

    initializePersonsArrays() {
        if (!!this.content) {
            if (this.content?.globalCtm) {
                this.addPersonsArray('globalCtm', this.content?.globalCtm);
            }

            if (this.content?.regionalCtms) {
                this.addPersonsArray('regionalCtms', this.content?.regionalCtms);
            }

            if (this.content?.pc) {
                this.addPersonsArray('pc', this.content?.pc);
            }

            if (this.content?.globalRsm) {
                this.addPersonsArray('globalRsm', this.content?.globalRsm);
            }

            if (this.content?.prm) {
                this.addPersonsArray('prm', this.content?.prm);
            }
        }
    }

    addEmptyPersonControl(roleName: string) {
        let formGroup = this.parentFormGroup.controls[roleName + 'FormGroup'] as FormGroup;
        let array = formGroup.controls.formArray as FormArray;

        array.push(
            this.formBuilder.group({
                fullName: new FormControl('', [Validators.required, notWhitespaceValidator(), maxLengthValidator(150)]),
                regionsDropdown: new FormControl(''),
            })
        );
    }

    public removePerson(roleName: string, index: number) {
        let formGroup = this.parentFormGroup.controls[roleName + 'FormGroup'] as FormGroup;
        let array = formGroup.controls.formArray as FormArray;
        array.removeAt(index);

        this.inputChangeEvent.emit({ target: roleName + '_' + index, value: null });
    }

    addPersonsArray(roleName: string, persons: ClinTrakPersonViewModel[]): void {
        let newFormGroup = this.formBuilder.group({
            formArray: new FormArray<FormControl<string>>([]),
        });

        this.parentFormGroup.addControl(roleName + 'FormGroup', newFormGroup);
        let formGroup = this.parentFormGroup.controls[roleName + 'FormGroup'] as FormGroup;
        let array = formGroup.controls.formArray as FormArray;

        array.clear();

        persons?.forEach((globalCtm, i) => {
            array.push(
                this.formBuilder.group({
                    fullName: new FormControl<string>('', [
                        Validators.required,
                        notWhitespaceValidator(),
                        maxLengthValidator(150),
                    ]),
                    regionsDropdown: new FormControl<string>('', [notWhitespaceValidator()]),
                })
            );
        });
    }

    initializeFormGroup() {
        this.travelOptionsFormGroup = this.formBuilder.group({
            travelOptionsDropdown: ['', [Validators.required, notWhitespaceValidator()]],
        });

        this.datesFormGroup = new FormGroup({
            dateFPFV: new PersistentFormControl(<Moment>undefined, [Validators.required]),
            dateLPLV: new PersistentFormControl(<Moment>undefined, [Validators.required]),
        });
        this.datesFormGroup.controls.dateLPLV.valueChanges
            .pipe(
                takeUntil(this.componentDestroyed$),
                tap((_) => {
                    this.setPassportDatesValidation();
                })
            )
            .subscribe();

        if (this.parentFormGroup) {
            this.parentFormGroup.addControl('countriesDropdown', this.countriesFormGroup);
            this.parentFormGroup.addControl('travelOptionsFormGroup', this.travelOptionsFormGroup);
            this.parentFormGroup.addControl('datesFormGroup', this.datesFormGroup);
            this.parentFormGroup.addControl(
                'sitesControl',
                new FormControl('', [
                    Validators.required,
                    NumberFormatValidator,
                    notWhitespaceValidator(),
                    maxLengthValidator(10),
                ])
            );
            this.parentFormGroup.addControl(
                'numPatients',
                new FormControl('', [
                    Validators.required,
                    NumberFormatValidator,
                    notWhitespaceValidator(),
                    maxLengthValidator(10),
                ])
            );
        }
    }

    setValues() {
        if (this.content) {
            const tempCountries = this.content?.studyCountries?.map((x) => x.countryId);
            this.countriesFormGroup.controls.countriesDropdown.setValue(tempCountries);

            const tempTravelOptions = this.content?.studyTravelOptions?.map((x) => x.id);
            this.travelOptionsFormGroup.controls.travelOptionsDropdown.setValue(tempTravelOptions);

            this.datesFormGroup.controls.dateFPFV.setValue(this.content.fpfv);
            this.datesFormGroup.controls.dateLPLV.setValue(this.content.lplv);

            this.setRoleFormArray('globalCtm', this.content?.globalCtm);
            this.setRoleFormArray('regionalCtms', this.content?.regionalCtms);
            this.setRoleFormArray('pc', this.content?.pc);
            this.setRoleFormArray('globalRsm', this.content?.globalRsm);
            this.setRoleFormArray('prm', this.content?.prm);

            this.parentFormGroup.controls.sitesControl.setValue(this.content.numSites);
            this.parentFormGroup.controls.numPatients.setValue(this.content.numPatients);
        }
    }

    setRoleFormArray(roleName: string, persons: ClinTrakPersonViewModel[]): void {
        let formGroup = this.parentFormGroup?.controls[roleName + 'FormGroup'] as FormGroup;
        let array = formGroup?.controls.formArray as FormArray<FormGroup>;

        if (array?.controls.length > 0 && persons?.length > 0) {
            array?.controls.forEach((controlGroup, index) => {
                switch (roleName) {
                    case 'pc':
                        controlGroup.controls.fullName.setValue(`${persons[index]?.fullName}`);
                        controlGroup.controls.regionsDropdown.setValue(persons[index]?.regionsId);
                        this.emitLocaleData(persons, index, roleName);
                        break;
                    case 'regionalCtms':
                        controlGroup.controls.fullName.setValue(`${persons[index]?.fullName}`);
                        controlGroup.controls.regionsDropdown.setValue(persons[index]?.regionsId);
                        this.emitLocaleData(persons, index, roleName);
                        break;
                    default:
                        controlGroup.controls.fullName.setValue(`${persons[index]?.fullName}`);
                        break;
                }
            });
        }
    }

    emitLocaleData(persons: ClinTrakPersonViewModel[], index: number, roleName: string) {
        if (
            persons[index]?.regionsId?.length > 0 &&
            (!persons[index]?.studyPersonLocales || persons[index]?.studyPersonLocales.length === 0)
        ) {
            let tempArray = [];

            persons[index]?.regionsId.forEach((id: string) => {
                let newLocaleModel = <StudyPersonLocaleViewModel>{
                    studyPersonId: '0',
                    regionId: id,
                };

                tempArray.push(newLocaleModel);
            });
            this.inputChangeEvent.emit({
                target: roleName + '_' + index + '-Regions',
                value: tempArray,
            });
        }
    }

    setPassportDatesValidation() {
        if (this.datesFormGroup.controls.dateLPLV.value?.toDate()) {
            this.datesFormGroup.controls.dateFPFV.setValidators([
                Validators.required,
                beforeDateValidator(
                    this.datesFormGroup.controls.dateLPLV.value.toDate(),
                    'FPFV date should be smaller than LPLV date!'
                ),
            ]);
            this.datesFormGroup.controls.dateFPFV.updateValueAndValidity();
        }
    }

    tryRemoveVisitName(removeOptionData: RemoveOptionDataWithParentId<VisitName>, index?: number) {
        let removeOptionWithIndex = <RemoveOptionDataWithIndexAndParentId>{
            options: removeOptionData.options,
            optionToRemove: removeOptionData.optionToRemove,
            index: index,
        };
        this.tryRemoveVisitNameSubject.next(removeOptionWithIndex);
    }

    tryRemoveVisitGroup(removeOptionData: RemoveOptionDataWithParentId<VisitName>, index?: number) {
        let removeOptionWithIndex = <RemoveOptionDataWithIndexAndParentId>{
            options: removeOptionData.options,
            optionToRemove: removeOptionData.optionToRemove,
            parentId: removeOptionData.parentId,
            index: index,
        };
        this.tryRemoveVisitGroupSubject.next(removeOptionWithIndex);
    }

    emitVisitScheduleChange: (value: VisitName[], target: Target, groupName: string) => void = (
        value: VisitName[],
        target: Target,
        groupName: string
    ) => {
        this.visitGroups[groupName] = value;
        let allVisits = <VisitName[]>[].concat(...Object.values(this.visitGroups));
        this.inputChangeEvent.emit({ target: target.id, value: allVisits });
    };

    emitChange: (value: any, target: Target) => void = (value: string, target: Target) => {
        this.inputChangeEvent.emit({ target: target.id, value: value });
    };
    emitStringChange(event: Event, target: Target) {
        const value = (event.target as HTMLInputElement).value;
        this.inputChangeEvent.emit({ target: target.id, value: value });
    }
    emitDateChange(event: MatDatepickerInputEvent<Moment>, target: Target) {
        const value = event.target.value;
        this.inputChangeEvent.emit({ target: target.id, value: value });
    }

    emitTravelOptions: (value: number[], target: Target) => void = (value: number[], target: Target) => {
        this.selectedTravelOptions = [];

        value.forEach((id) => {
            let travelOption = this.travelOptions.find((element) => element.id === id);
            this.selectedTravelOptions.push(travelOption);
        });
        this.inputChangeEvent.emit({ target: target.id, value: this.selectedTravelOptions });
    };

    handleTravelOptionsDropdownValidation() {
        //all studies are Full Service: travel options are always required
        this.travelOptionsFormGroup?.controls?.travelOptionsDropdown?.addValidators([Validators.required]);
        this.travelOptionsFormGroup?.controls?.travelOptionsDropdown.updateValueAndValidity();
    }

    countriesSelectionChange(event: MatSelectChange, id: string) {
        this.selectedCountries = [];

        event.value.forEach((id: string) => {
            let country = this.countries.find((element) => element.countryId === id);
            this.selectedCountries.push(country);
        });
        this.inputChangeEvent.emit({ target: id, value: this.selectedCountries });
    }

    regionsSelectionChange(event: MatSelectChange, id: string) {
        this.selectedRegions = [];
        let tempArray = [];
        let personType = id.substring(0, id.indexOf('_'));
        let index = +id.substring(id.indexOf('_') + 1, id.indexOf('-'));

        event.value.forEach((id: string) => {
            let region = this.regions.find((element) => element.regionId === id);
            let existingStudyPerson = this.content[personType][index];
            let spL = <StudyPersonLocaleViewModel>(
                existingStudyPerson?.studyPersonLocales?.find((x) => x.regionId == region.regionId)
            );

            if (spL) tempArray.push(spL);
            else {
                let newLocaleModel = <StudyPersonLocaleViewModel>{
                    studyPersonId: existingStudyPerson?.id,
                    regionId: region.regionId,
                };

                tempArray.push(newLocaleModel);
            }
        });
        this.inputChangeEvent.emit({ target: id, value: tempArray });
    }

    getFormArray(role: string): FormArray<FormGroup> {
        switch (role) {
            case 'globalCtm':
                return this.getFormGroup('globalCtmFormGroup')?.controls.formArray as FormArray<FormGroup>;
            case 'regionalCtms':
                return this.getFormGroup('regionalCtmsFormGroup')?.controls.formArray as FormArray<FormGroup>;
            case 'pc':
                return this.getFormGroup('pcFormGroup')?.controls.formArray as FormArray<FormGroup>;
            case 'globalRsm':
                return this.getFormGroup('globalRsmFormGroup')?.controls.formArray as FormArray<FormGroup>;
            case 'prm':
                return this.getFormGroup('prmFormGroup')?.controls.formArray as FormArray<FormGroup>;
        }
    }

    sortAsc = sortAsc;

    getFormGroup(formGroupName: string): FormGroup {
        return this.parentFormGroup.controls[formGroupName] as FormGroup;
    }

    getFullNameFormControl(personFormGroup: FormGroup): FormControl<string> {
        return personFormGroup.controls.fullName as FormControl<string>;
    }

    getRegionsFormControl(personFormGroup: FormGroup): FormControl {
        return personFormGroup.controls.regionsDropdown as FormControl;
    }

    getCheckboxesArray(): FormArray {
        return this.parentFormGroup.get('studyExpenses') as FormArray;
    }

    checkboxesSelectChanged($event: MatCheckboxChange) {
        let updatedItem = this.content?.expenseTypes.find((x) => x.expenseKey === $event.source.name);
        updatedItem.active = $event.checked;
        this.emitChange(this.content?.expenseTypes, { id: 'expenseTypes' });
    }

    getActiveStudyExpenses() {
        return this.content?.expenseTypes.filter((x) => x.active);
    }
}
